import { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react'
import { siteData } from '../../data/siteData'

export default function Contact() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const formRef = useRef<HTMLFormElement>(null)

  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    participants: '',
    eventType: 'Inne',
    message: '',
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('sending')

    try {
      const res = await fetch('/send-mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })

      if (!res.ok) {
        const text = await res.text()
        console.error('Błąd HTTP', res.status, text)
        setStatus('error')
        setTimeout(() => setStatus('idle'), 5000)
        return
      }

      const data = await res.json()

      if (data.success) {
        setStatus('success')
        setFormData({
          name: '', phone: '', email: '', date: '', participants: '', eventType: 'Inne', message: '',
        })
      } else {
        console.error('Błąd serwera:', data.message)
        setStatus('error')
      }
      setTimeout(() => setStatus('idle'), 5000)
    } catch (err) {
      console.error('Błąd sieci / fetch:', err)
      setStatus('error')
      setTimeout(() => setStatus('idle'), 5000)
    }
  }

  const contactInfo = [
    { icon: Phone, label: 'Telefon', value: siteData.phone, href: `tel:${siteData.phone}` },
    { icon: Mail, label: 'Email', value: siteData.email, href: `mailto:${siteData.email}` },
    { icon: MapPin, label: 'Adres', value: siteData.address, href: '#' },
    { icon: Clock, label: 'Godziny', value: siteData.hours, href: '#' },
  ]

  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-section-dark grain-overlay" style={{ backgroundColor: 'rgba(10,14,10,0.55)', backdropFilter: 'blur(2px)' }}>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-military/40 to-transparent" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="font-mono text-xs text-military-light tracking-[0.4em] uppercase">
            Kontakt
          </span>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl text-bone mt-4 tracking-wider">
            GOTOWY DO AKCJI?
          </h2>
          <p className="font-body text-chrome mt-4 max-w-2xl mx-auto">
            Jeden krok dzieli Cię od misji.
          </p>
          <div className="w-24 h-0.5 bg-military-light mx-auto mt-6" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-mono text-xs text-chrome uppercase tracking-wider mb-2">
                    Imię i nazwisko *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-steel-light border border-military/20 rounded-sm text-bone font-body text-sm placeholder:text-chrome/50 focus:border-military-light focus:outline-none focus:ring-1 focus:ring-military-light/30 transition-colors"
                    placeholder="Jan Kowalski"
                  />
                </div>
                <div>
                  <label className="block font-mono text-xs text-chrome uppercase tracking-wider mb-2">
                    Numer telefonu *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-steel-light border border-military/20 rounded-sm text-bone font-body text-sm placeholder:text-chrome/50 focus:border-military-light focus:outline-none focus:ring-1 focus:ring-military-light/30 transition-colors"
                    placeholder="+48 000 000 000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-mono text-xs text-chrome uppercase tracking-wider mb-2">
                    Adres e-mail
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-steel-light border border-military/20 rounded-sm text-bone font-body text-sm placeholder:text-chrome/50 focus:border-military-light focus:outline-none focus:ring-1 focus:ring-military-light/30 transition-colors"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block font-mono text-xs text-chrome uppercase tracking-wider mb-2">
                    Data eventu
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-steel-light border border-military/20 rounded-sm text-bone font-body text-sm focus:border-military-light focus:outline-none focus:ring-1 focus:ring-military-light/30 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-mono text-xs text-chrome uppercase tracking-wider mb-2">
                    Liczba uczestników
                  </label>
                  <input
                    type="number"
                    name="participants"
                    min="1"
                    value={formData.participants}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-steel-light border border-military/20 rounded-sm text-bone font-body text-sm placeholder:text-chrome/50 focus:border-military-light focus:outline-none focus:ring-1 focus:ring-military-light/30 transition-colors"
                    placeholder="np. 10"
                  />
                </div>
                <div>
                  <label className="block font-mono text-xs text-chrome uppercase tracking-wider mb-2">
                    Typ eventu
                  </label>
                  <select
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-steel-light border border-military/20 rounded-sm text-bone font-body text-sm focus:border-military-light focus:outline-none focus:ring-1 focus:ring-military-light/30 transition-colors appearance-none"
                  >
                    <option value="Kawalerski">Wieczór kawalerski</option>
                    <option value="Firmowy">Impreza firmowa</option>
                    <option value="Urodziny">Urodziny</option>
                    <option value="Turniej">Turniej ASG</option>
                    <option value="Rodzinne">Rodzinne wyjście</option>
                    <option value="Szkolne">Event szkolny</option>
                    <option value="Inne">Inne</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-mono text-xs text-chrome uppercase tracking-wider mb-2">
                  Dodatkowe informacje
                </label>
                <textarea
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-steel-light border border-military/20 rounded-sm text-bone font-body text-sm placeholder:text-chrome/50 focus:border-military-light focus:outline-none focus:ring-1 focus:ring-military-light/30 transition-colors resize-none"
                  placeholder="Opisz swoje oczekiwania, pytania, specjalne życzenia..."
                />
              </div>

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full py-4 bg-military-light text-bone font-subheading font-bold text-base uppercase tracking-widest rounded-sm hover:bg-military-glow transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {status === 'sending' ? (
                  <>
                    <div className="w-5 h-5 border-2 border-bone/30 border-t-bone rounded-full animate-spin" />
                    Wysyłanie...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Wyślij zapytanie
                  </>
                )}
              </button>

              {/* Status Messages */}
              <AnimatePresence>
                {status === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-3 p-4 bg-success/10 border border-success/30 rounded-sm"
                  >
                    <CheckCircle size={20} className="text-success flex-shrink-0" />
                    <p className="font-body text-sm text-bone">
                      Oddzwonimy do Ciebie w ciągu 24 godzin.
                    </p>
                  </motion.div>
                )}
                {status === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-3 p-4 bg-danger/10 border border-danger/30 rounded-sm"
                  >
                    <AlertCircle size={20} className="text-danger flex-shrink-0" />
                    <p className="font-body text-sm text-bone">
                      Wystąpił błąd. Spróbuj ponownie lub zadzwoń bezpośrednio.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-6"
          >
            {contactInfo.map((item, i) => {
              const Icon = item.icon
              return (
                <motion.a
                  key={i}
                  href={item.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.4 + i * 0.1 }}
                  className="flex items-start gap-4 p-5 bg-steel-light border border-military/20 rounded-sm hover:border-military/40 transition-colors group"
                >
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-military/10 border border-military/30 rounded-sm group-hover:bg-military/20 transition-colors">
                    <Icon size={20} className="text-military-light" />
                  </div>
                  <div>
                    <p className="font-mono text-[10px] text-chrome uppercase tracking-wider mb-1">
                      {item.label}
                    </p>
                    <p className="font-subheading font-semibold text-bone group-hover:text-military-light transition-colors">
                      {item.value}
                    </p>
                  </div>
                </motion.a>
              )
            })}

            {/* Social */}
            <div className="pt-6 border-t border-military/20">
              <p className="font-mono text-[10px] text-chrome uppercase tracking-wider mb-4">
                Social media
              </p>
              <div className="flex gap-3">
                {Object.entries(siteData.social).map(([name, url]) => (
                  <a
                    key={name}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-steel-lighter border border-military/20 rounded-sm font-mono text-xs text-chrome hover:text-bone hover:border-military-light transition-colors capitalize"
                  >
                    {name}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
